/**
 * Очікування області (UX-32; states.md, розд. 2):
 * - до 300 мс — нічого: лишається старий вміст, екран не блимає;
 * - 300 мс – 10 с — скелетон саме цієї області у формі майбутнього вмісту;
 * - понад 3 с — під скелетоном «Довше, ніж зазвичай…»;
 * - понад 10 с — стан помилки області зі «Спробувати ще раз» і кодом для підтримки.
 * Програма читання чує «Завантаження…» один раз на появу скелетона, а через 3 с — «Довше, ніж
 * зазвичай…» (живий регіон сервісу зливає однакові оголошення кількох областей). Скелетон фокусу не отримує
 * (`aria-hidden`), пульсація — лише `opacity`, у легкому режимі й при зменшеному русі — статичний.
 * Дані прийшли раніше — компонент просто зникає разом із `isPending` у викликача.
 */
import { type ReactNode, useEffect, useState } from 'react';
import { common as c } from '../i18n/uk/common.ts';
import { states as t } from '../i18n/uk/states.ts';
import { announce } from './notify/store.ts';
import { StateView } from './StateView.tsx';

export const PENDING_DELAY_MS = 300;
export const PENDING_SLOW_MS = 3_000;
export const PENDING_TIMEOUT_MS = 10_000;

export type PendingPhase = 'wait' | 'skeleton' | 'slow' | 'timeout';

/**
 * Розклад фаз без React: викликає `onPhase` на 300 мс, 3 с і 10 с; повертає скасування.
 * Окремо від компонента, щоб пороги перевіряти на `vi.useFakeTimers()` (хелпери рендера з ними
 * не працюють).
 */
export function schedulePhases(onPhase: (phase: Exclude<PendingPhase, 'wait'>) => void) {
  const timers = [
    setTimeout(() => onPhase('skeleton'), PENDING_DELAY_MS),
    setTimeout(() => onPhase('slow'), PENDING_SLOW_MS),
    setTimeout(() => onPhase('timeout'), PENDING_TIMEOUT_MS),
  ];
  return () => {
    for (const timer of timers) clearTimeout(timer);
  };
}

export type PhaseSchedule = typeof schedulePhases;

/** Фаза очікування від монтування (або від `restart`). `schedule` — для тестів без таймерів. */
export function usePendingPhase(
  restart = 0,
  schedule: PhaseSchedule = schedulePhases,
): PendingPhase {
  const [phase, setPhase] = useState<PendingPhase>('wait');
  // biome-ignore lint/correctness/useExhaustiveDependencies: restart — навмисний тригер нового відліку
  useEffect(() => {
    setPhase('wait');
    return schedule(setPhase);
  }, [restart, schedule]);
  return phase;
}

/** Код для підтримки, коли відповіді немає понад 10 с (states.md, розд. 3): клієнтський тайм-аут. */
export const PENDING_TIMEOUT_CODE = 'APP-408';

interface PendingProps {
  /** Скелетон області у формі вмісту (`SkeletonTable`, `SkeletonLines`…). */
  skeleton: ReactNode;
  /** Повтор після 10 с: зазвичай `query.refetch()`. Без нього стан помилки без кнопки. */
  onRetry?: () => void;
  /**
   * Розклад фаз; типово — таймери `schedulePhases`. Тест передає ручний розклад і викликає фази
   * сам: хелпери рендера не працюють з `vi.useFakeTimers()`, а справжній час чекати заборонено
   * (test-strategy.md, розд. 2.1). Значення має бути сталим між рендерами.
   */
  schedule?: PhaseSchedule;
}

export function Pending({ skeleton, onRetry, schedule }: PendingProps) {
  const [restart, setRestart] = useState(0);
  const phase = usePendingPhase(restart, schedule);

  useEffect(() => {
    if (phase === 'skeleton') announce(t.loading);
    if (phase === 'slow') announce(t.loadingSlow);
  }, [phase]);

  if (phase === 'wait') return null;
  if (phase === 'timeout') {
    return (
      <PendingTimeout
        onRetry={
          onRetry &&
          (() => {
            setRestart((n) => n + 1);
            onRetry();
          })
        }
      />
    );
  }
  return (
    <>
      {skeleton}
      {phase === 'slow' && <PendingSlow />}
    </>
  );
}

/** «Довше, ніж зазвичай…» під скелетоном (з 3 с) — спільне з `<Area>`. */
export function PendingSlow() {
  return (
    <p className="pending__slow" data-testid="pending-slow">
      {t.loadingSlow}
    </p>
  );
}

/** Тайм-аут області (понад 10 с): стан помилки з кодом і «Спробувати ще раз» — спільне з `<Area>`. */
export function PendingTimeout({ onRetry }: { onRetry?: (() => void) | undefined }) {
  return (
    <StateView
      icon="clock"
      tone="error"
      title={t.timeoutTitle}
      text={t.timeoutText}
      code={PENDING_TIMEOUT_CODE}
    >
      {onRetry && (
        <button className="btn btn--primary" type="button" onClick={onRetry}>
          {c.retry}
        </button>
      )}
    </StateView>
  );
}
